// src/components/QueryInput/QueryDisplay.js
import React, { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { Box, Typography, Divider, Button } from '@mui/material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

const CodeBlock = React.memo(function CodeBlock({ code, language }) {
  // Maintain internal state for the displayed code
  const [finalCode, setFinalCode] = useState(code);

  // Only update if the new code is "greater than or equal to" 
  // the existing code (by length).
  useEffect(() => {
    if (code != null && code.length >= (finalCode?.length || 0)) {
      setFinalCode(code);
    }
  }, [code, finalCode]);

  return (
    <Box sx={{ marginBottom: '20px' }}>
      <SyntaxHighlighter language={language} style={vscDarkPlus}>
        {finalCode}
      </SyntaxHighlighter>
    </Box>
  );
});

const FileActions = React.memo(function FileActions({
  label,
  disabled,
  onClick
}) {
  return (
    <Button
      variant="contained"
      sx={{
        backgroundColor: '#303030',
        color: 'white',
        '&:hover': {
          backgroundColor: '#444444',
        },
      }}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </Button>
  );
});

const FileItem = React.memo(function FileItem({
  item,
  isDependencySection,
  onAction,
  loading,
  mimeTypeToLanguage
}) {
  const language = useMemo(
    () => mimeTypeToLanguage(item.mimetype),
    [item.mimetype, mimeTypeToLanguage]
  );

  if (isDependencySection || (item.updates && item.updates.length > 0) || item.content) {
    return (
      <Box sx={{ marginTop: '10px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Typography sx={{ color: 'white', fontWeight: 'bold' }}>
            {item.filename || (isDependencySection ? 'Dependencies' : 'Content')}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {onAction && (
            <FileActions
              label={
                isDependencySection
                  ? 'Install'
                  : item.content
                  ? 'Create'
                  : 'Apply'
              }
              onClick={onAction}
              disabled={loading}
            />
          )}
        </Box>
        {item.content && (
          <CodeBlock code={item.content} language={language} />
        )}
        {item.updates && (
          <Box sx={{ marginTop: '10px' }}>
            {item.updates.map((pair, idx) => (
              <Typography key={idx} sx={{ color: 'white' }}>
                <CodeBlock code={pair.code_snippet} language={language} />
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  }
  return null;
});

const DependencySection = React.memo(function DependencySection({
  dependencies,
  onInstall,
  loading
}) {
  return (
    <Box sx={{ marginTop: '10px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Typography sx={{ color: 'white', fontWeight: 'bold' }}>
          Dependencies
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <FileActions label="Install" onClick={onInstall} disabled={loading} />
      </Box>
      <CodeBlock code={dependencies.join('\n')} language="shell" />
    </Box>
  );
});

const ContentRenderer = React.memo(function ContentRenderer({
  content,
  createFile,
  handleApplyChanges,
  addDependencies,
  loading,
  mimeTypeToLanguage
}) {
  if (typeof content === 'string') {
    return (
      <Typography sx={{ color: 'white', marginLeft: '10px' }}>
        {content}
      </Typography>
    );
  }

  if (typeof content === 'object') {
    return (
      <>
        {Object.entries(content).map(([key, value]) => {
          if (Array.isArray(value)) {
            if (
              key === 'dependencies_to_add' &&
              value.length > 0 &&
              !(value.length === 1 && value[0] === '')
            ) {
              // Dependencies Section
              return (
                <DependencySection
                  key={key}
                  dependencies={value}
                  onInstall={() => addDependencies(value)}
                  loading={loading}
                />
              );
            } else if (
              (key === 'new_files' || key === 'file_changes') &&
              value.length > 0 &&
              !(value.length === 1 && value[0] === '')
            ) {
              // Files section
              return value.map((item, idx) => {
                // Prefer a stable key if possible (like an ID or filename)
                const stableKey = item.filename || `${key}-${idx}`;
                return (
                  <FileItem
                    key={stableKey}
                    item={item}
                    onAction={() => {
                      if (key === 'new_files') {
                        createFile(item.filename, item.content, item.mimetype);
                      } else {
                        handleApplyChanges(item);
                      }
                    }}
                    loading={loading}
                    mimeTypeToLanguage={mimeTypeToLanguage}
                  />
                );
              });
            }
          } else {
            return (
              <Typography key={key} sx={{ color: 'white' }}>
                {value}
              </Typography>
            );
          }
          return null;
        })}
      </>
    );
  }

  return null;
});

const QueryDisplay = ({
  responseContent,
  loading,
  createFile,
  handleApplyChanges,
  addDependencies,
  mimeTypeToLanguage
}) => {
  // Memoize this function so it doesn't re-create on every render
  const memoizedMimeTypeToLanguage = useCallback(
    (mimetype) => mimeTypeToLanguage(mimetype),
    [mimeTypeToLanguage]
  );

  // 1) Use a ref to capture the scrollable container or an end marker
  const scrollContainerRef = useRef(null);

  // 2) Scroll to bottom whenever responseContent changes
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // Option A: Instant jump
      // container.scrollTop = container.scrollHeight;

      // Option B: Smooth scroll
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [responseContent]);

  return (
    <Box
      // 3) Attach ref here
      ref={scrollContainerRef}
      className="queryInputMessages"
      sx={{
        maxHeight: '78vh', // or whatever max size you want
        overflowY: 'auto',
        '&::-webkit-scrollbar': { display: 'none' },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
      }}
    >
      {responseContent.map((content, index) => {
        // Provide a stable key if available
        const stableKey = `resp-${index}`;
        const showDivider = index > 0 && content.query;

        return (
          <Box key={stableKey} sx={{ marginBottom: '10px' }}>
            {showDivider && (
              <Divider
                sx={{
                  backgroundColor: 'white',
                  marginBottom: '15px',
                }}
              />
            )}
            {content.query && (
              <Typography sx={{ color: 'white', fontWeight: 'bold' }}>
                {content.query}
              </Typography>
            )}
            <ContentRenderer
              content={content.response}
              createFile={createFile}
              handleApplyChanges={handleApplyChanges}
              addDependencies={addDependencies}
              loading={loading}
              mimeTypeToLanguage={memoizedMimeTypeToLanguage}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default React.memo(QueryDisplay);
